import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ProductCard from '../components/ProductCard';
import { getProdutos } from '../services/api';
import {
  CATALOGO_TOTAL_PAGINAS,
  getCatalogoPageImageUrl,
  INTRO_CATALOGO,
} from '../data/catalogoAggily2025';
import './Produtos.css';
import './Lancamentos.css';

const ITEMS_PER_PAGE = 12;
const SORT_OPTIONS = [
  { value: 'recentes', label: 'Mais recentes' },
  { value: 'preco_asc', label: 'Menor preço' },
  { value: 'preco_desc', label: 'Maior preço' },
  { value: 'nome', label: 'Nome A-Z' },
];

const Lancamentos = () => {
  const [produtos, setProdutos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategoria, setSelectedCategoria] = useState('');
  const [sortOrder, setSortOrder] = useState('recentes');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const categoria = searchParams.get('categoria');
    if (categoria) {
      setSelectedCategoria(categoria);
    }
    loadProdutos();
  }, [searchParams]);

  const loadProdutos = async () => {
    try {
      const response = await getProdutos();
      const data = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      setProdutos(data || []);
      setLoading(false);
    } catch (error) {
      console.error('Erro ao carregar lançamentos:', error);
      setLoading(false);
    }
  };

  const produtosOrdenados = useMemo(() => {
    return [...produtos].sort((a, b) => {
      const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
      const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
      return dateB - dateA;
    });
  }, [produtos]);

  const categorias = [...new Set(produtosOrdenados.map((p) => p.categoria).filter(Boolean))];

  const filteredProdutos = produtosOrdenados.filter((produto) => {
    const matchSearch =
      produto.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (produto.descricao && produto.descricao.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchCategoria = !selectedCategoria || produto.categoria === selectedCategoria;
    return matchSearch && matchCategoria;
  });

  const sorted = useMemo(() => {
    return [...filteredProdutos].sort((a, b) => {
      if (sortOrder === 'recentes') {
        const da = a.created_at ? new Date(a.created_at) : new Date(0);
        const db = b.created_at ? new Date(b.created_at) : new Date(0);
        return db - da;
      }
      if (sortOrder === 'preco_asc') return parseFloat(a.preco) - parseFloat(b.preco);
      if (sortOrder === 'preco_desc') return parseFloat(b.preco) - parseFloat(a.preco);
      if (sortOrder === 'nome') return (a.nome || '').localeCompare(b.nome || '');
      return 0;
    });
  }, [filteredProdutos, sortOrder]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / ITEMS_PER_PAGE));
  const pageStart = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedProdutos = sorted.slice(pageStart, pageStart + ITEMS_PER_PAGE);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategoria, sortOrder]);

  return (
    <div className="produtos-page lancamentos-page">
      <Helmet>
        <title>Lançamentos | Loucas por Beleza</title>
        <meta name="description" content="Novidades e lançamentos em produtos de beleza. Catálogo Aggily 2025 e últimas tendências." />
      </Helmet>
      <div className="container">
        {/* Catálogo Aggily 2025 - páginas iguais ao PDF */}
        <section className="catalogo-aggily">
          <div className="catalogo-aggily-header">
            <h2 className="catalogo-aggily-title">Catálogo Aggily 2025</h2>
            <p className="catalogo-aggily-intro">{INTRO_CATALOGO}</p>
          </div>
          <div className="catalogo-paginas">
            {Array.from({ length: CATALOGO_TOTAL_PAGINAS }, (_, i) => i + 1).map((num) => (
              <div key={num} className="catalogo-pagina-wrapper">
                <img
                  src={getCatalogoPageImageUrl(num)}
                  alt={`Catálogo Aggily 2025 - Página ${num}`}
                  className="catalogo-pagina-img"
                  loading="lazy"
                />
                <span className="catalogo-pagina-num">Página {num}</span>
              </div>
            ))}
          </div>
        </section>

        <div className="produtos-header">
          <h1 className="page-title">Lançamentos</h1>
          <div className="produtos-header-actions">
            <span className="results-count">
              {filteredProdutos.length}{' '}
              {filteredProdutos.length === 1 ? 'produto encontrado' : 'produtos encontrados'}
            </span>
            <label className="sort-label">
              Ordenar:
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="sort-select"
              >
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </label>
          </div>
        </div>

        <div className="produtos-layout">
          {/* Sidebar de Filtros */}
          <aside className="filters-sidebar">
            <div className="filter-section">
              <h3 className="filter-title">Buscar</h3>
              <input
                type="text"
                placeholder="Buscar lançamentos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            {categorias.length > 0 && (
              <div className="filter-section">
                <h3 className="filter-title">Categorias</h3>
                <div className="filter-options">
                  <label className="filter-option">
                    <input
                      type="radio"
                      name="categoria"
                      value=""
                      checked={selectedCategoria === ''}
                      onChange={(e) => setSelectedCategoria(e.target.value)}
                    />
                    <span>Todas</span>
                  </label>
                  {categorias.map((categoria) => (
                    <label key={categoria} className="filter-option">
                      <input
                        type="radio"
                        name="categoria"
                        value={categoria}
                        checked={selectedCategoria === categoria}
                        onChange={(e) => setSelectedCategoria(e.target.value)}
                      />
                      <span>{categoria}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {selectedCategoria && (
              <button className="clear-filters" onClick={() => setSelectedCategoria('')}>
                Limpar filtros
              </button>
            )}
          </aside>

          {/* Área de Produtos */}
          <main className="produtos-main">
            {loading ? (
              <div className="loading">Carregando lançamentos...</div>
            ) : filteredProdutos.length > 0 ? (
              <>
                <div className="products-grid-mercadolivre">
                  {paginatedProdutos.map((produto) => (
                    <ProductCard key={produto.id} produto={produto} />
                  ))}
                </div>
                {totalPages > 1 && (
                  <div className="pagination">
                    <button
                      type="button"
                      className="pagination-btn"
                      disabled={currentPage <= 1}
                      onClick={() => setCurrentPage((p) => p - 1)}
                    >
                      Anterior
                    </button>
                    <span className="pagination-info">
                      Página {currentPage} de {totalPages}
                    </span>
                    <button
                      type="button"
                      className="pagination-btn"
                      disabled={currentPage >= totalPages}
                      onClick={() => setCurrentPage((p) => p + 1)}
                    >
                      Próxima
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="no-products">
                {searchTerm || selectedCategoria
                  ? 'Nenhum lançamento encontrado para sua busca.'
                  : 'Nenhum lançamento cadastrado ainda.'}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default Lancamentos;


